import { useEffect } from 'react';

/**
 * Hook to prevent scroll chaining to parent elements when scrollable element
 * reaches its scroll boundaries (top or bottom)
 *
 * This prevents the page from scrolling when the user tries to scroll past
 * the boundaries of a modal or scrollable container.
 *
 * @param {React.RefObject} scrollRef - Ref to the scrollable element
 * @param {boolean} isEnabled - Whether prevention is enabled (default: true)
 *
 * @example
 * const scrollRef = useRef(null);
 * useScrollChainPrevention(scrollRef, isModalOpen);
 *
 * <div ref={scrollRef}>Scrollable content</div>
 */
export const useScrollChainPrevention = (scrollRef, isEnabled = true) => {
    useEffect(() => {
        if (!isEnabled || !scrollRef.current) {
            return undefined;
        }

        const preventScrollChaining = e => {
            const element = e.currentTarget;

            const goingUp = e.deltaY < 0;
            const goingDown = e.deltaY > 0;

            const epsilon = 2;

            const atTop = element.scrollTop <= 0;
            const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - epsilon;

            // Prevent scroll chaining when at boundaries
            if ((atTop && goingUp) || (atBottom && goingDown)) {
                e.preventDefault();
            }
        };

        const element = scrollRef.current;

        element.addEventListener('wheel', preventScrollChaining, { passive: false });

        return () => {
            element.removeEventListener('wheel', preventScrollChaining);
        };
    }, [scrollRef, isEnabled]);
};

export default useScrollChainPrevention;
